import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";
import { AuthService } from "@/services/authService";
import { currentUser } from "@/redux/reducers/AuthReducer";
import { resolveLandingPath } from "@/routes/landing";

export const MicrosoftIcon = () => (
  <svg width="18" height="18" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg" className="shrink-0">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

const MicrosoftCallback = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const error = params.get("error"); // ✅ add this
    const errorDescription = params.get("error_description"); // ✅ add this

    const storedState = localStorage.getItem("oauth_state");
    localStorage.removeItem("oauth_state");

    // ✅ Show the actual Microsoft error message
    if (error) {
      console.error("Microsoft OAuth error:", error, errorDescription);
      toast.error(errorDescription?.replace(/\+/g, " ") || "Microsoft sign in failed.");
      navigate("/login?error=oauth_failed");
      return;
    }

    if (!code || !state) {
      toast.error("Microsoft sign in was cancelled or failed.");
      navigate("/login?error=oauth_failed");
      return;
    }

    if (!storedState) {
      toast.error("Session expired. Please try signing in again.");
      navigate("/login?error=oauth_failed");
      return;
    }

    AuthService.handleMicrosoftCallback(code, state, storedState)
      .then(async (data) => {
        const result = await dispatch(currentUser());
        if (currentUser.fulfilled.match(result)) {
          toast.success(`Welcome, ${data.user?.name}!`);
          // Resolve the destination from the account that just signed in.
          // /admin/dashboard is wrong for a SuperAdmin (RouteGuard bounces them
          // to /super-admin/*, showing the tenant shell on the way) and wrong
          // for anyone whose role lacks dashboard.view — they would land on
          // "Unauthorized" straight after a successful sign-in.
          navigate(resolveLandingPath(result.payload?.user), { replace: true });
        } else {
          toast.error("Failed to load user profile. Please try again.");
          navigate("/login?error=oauth_failed");
        }
      })
      .catch((err) => {
        console.error("Microsoft callback error:", err?.response?.data);
        toast.error(err?.response?.data?.message || "Microsoft sign in failed. Please try again.");
        navigate("/login?error=oauth_failed");
      });
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#111111] text-white">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="animate-spin text-indigo-400" size={28} />
        <p className="text-sm text-white/60">Completing Microsoft sign in...</p>
      </div>
    </div>
  );
};

export default MicrosoftCallback;
